import React, { useCallback, useEffect, useState } from 'react'
import { ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTheme } from '@ui-kitten/components'
import { useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { useNavigation } from '@react-navigation/native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { LoadingIndicator } from '@src/components/LoadingIndicator'
import Analytics, { CustomEventTypes } from '@src/config/analytics'
import { getCoachingKindDescriptor } from '@src/utils/coach'
import { AllCoachProfilesQuery } from '@src/graphql/fetchAllCoachProfiles.generated'
import { RootStackParamList } from '@src/navigation/types'
import { useSnack } from '@src/utils/navigatorContext'
import { Button, Text } from '@src/components/base'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { CoachesCoachingKind, EhrAppointmentSchedulingMode } from '@src/types'
import { getSchedulingInfoForCoachingKind } from '../NextCall/useCallSchedulingState'
import { ehrAppointmentSchedulingInfoSelector } from '../../models/nutritionistHub.selectors'
import { useCoachSwitch } from './hooks/useCoachSwitch'
import { CoachCard } from './CoachCard'

interface CoachSwitchComponentProps {
  coachingKind?: CoachesCoachingKind
}

export const CoachSwitchComponent = ({
  coachingKind = CoachesCoachingKind.Mnt,
}: CoachSwitchComponentProps) => {
  const styles = useStyleSheet(themedStyles)
  const insets = useSafeAreaInsets()
  const showSnack = useSnack()
  const theme = useTheme()
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()

  const coachDescriptor = getCoachingKindDescriptor(coachingKind)

  const appointmentSchedulingInfo = useSelector(ehrAppointmentSchedulingInfoSelector)
  const coachingSchedulingInfo = getSchedulingInfoForCoachingKind(
    appointmentSchedulingInfo,
    coachingKind,
  )
  const insuranceCoveredMode =
    coachingSchedulingInfo?.schedulingMode === EhrAppointmentSchedulingMode.InsuranceCovered

  const [isDataLoading, setIsDataLoading] = useState(false)
  const [recommendedCoaches, setRecommendedCoaches] = useState<
    AllCoachProfilesQuery['allCoachProfiles']['coachProfiles']
  >([])

  const handleSeeAllCoaches = () => {
    navigation.navigate('CoachSwitchSeeAllCoachesScreen', {
      coachingKind,
      navigationTitle: `Switch ${coachDescriptor.capitalized}`,
      title: `Available ${coachDescriptor.capitalized.plural}`,
      loadMoreButtonText: 'Load More',
      showFilters: coachingKind !== CoachesCoachingKind.StressManagement,
    })
  }

  const { fetchCoachProfiles } = useCoachSwitch({ coachingKind })

  useLazyScreenPreload('CoachSwitchSeeAllCoachesScreen')

  useEffect(() => {
    Analytics.track(CustomEventTypes.CoachSwitchRecommendedScreenViewed)
  }, [])

  useEffect(() => {
    const loadCoachProfiles = async () => {
      try {
        setIsDataLoading(true)

        const response = await fetchCoachProfiles({
          recommended: true,
          appointmentKind: coachingSchedulingInfo?.options[0]?.appointmentKind,
        })
        setRecommendedCoaches(response.coachProfiles)
      } catch {
        showSnack(`Something went wrong`, null, 'error')
      } finally {
        setIsDataLoading(false)
      }
    }

    loadCoachProfiles()
  }, [coachingSchedulingInfo?.options, fetchCoachProfiles, showSnack])

  const coachingSchedulingInfoOptions = coachingSchedulingInfo?.options

  useLazyScreenPreload('CoachSwitchSummaryScreen', {
    shouldPreload: !insuranceCoveredMode,
  })

  const onCoachSelect = useCallback(
    (coachProfile: AllCoachProfilesQuery['allCoachProfiles']['coachProfiles'][0]) => {
      Analytics.track(CustomEventTypes.CoachSwitchSelectCoachTapped, {
        currentScreen: 'Recommended',
        insuranceCoveredMode,
      })

      if (!coachingSchedulingInfoOptions || coachingSchedulingInfoOptions.length === 0) {
        return
      }

      if (insuranceCoveredMode) {
        navigation.navigate('CallScheduling', {
          appointmentKind: coachingSchedulingInfoOptions[0].appointmentKind,
          allowBackNavigation: true,
          switchingCoach: true,
          coachProfile,
        })
        return
      }

      navigation.navigate('CoachSwitchSummaryScreen', {
        userInitiated: true,
        coachProfile,
        appointmentKind: coachingSchedulingInfoOptions[0].appointmentKind,
      })
    },
    [coachingSchedulingInfoOptions, insuranceCoveredMode, navigation],
  )

  if (isDataLoading) {
    return (
      <View style={[styles.container, { paddingBottom: insets.bottom }]}>
        <View style={styles.loadingContainer}>
          <LoadingIndicator size="large" color={theme['theme.primary.base']} />
        </View>
      </View>
    )
  }

  const singleRecommendation = recommendedCoaches.length === 1
  const matchedCoachPronoun = singleRecommendation ? 'this' : 'these'
  const matchedCoachDescriptor = singleRecommendation ? coachDescriptor : coachDescriptor.plural
  const title = `Based on your health goals, we’ve matched you with ${matchedCoachPronoun} ${matchedCoachDescriptor}.`

  return (
    <View style={[styles.container, { paddingBottom: insets.bottom }]}>
      <Text type="regular" style={styles.title}>
        {title}
      </Text>
      <ScrollView contentContainerStyle={styles.scrollList}>
        <View style={styles.cardList}>
          {recommendedCoaches.map((coachProfile) => (
            <CoachCard
              key={coachProfile.id}
              coachProfile={coachProfile}
              onCoachSelect={onCoachSelect}
            />
          ))}
        </View>
      </ScrollView>
      <Button
        size="block"
        type="outline"
        accessibilityLabel="See All"
        style={styles.seeAllButton}
        onPress={handleSeeAllCoaches}
      >
        {`See All ${coachDescriptor.capitalized.plural}`}
      </Button>
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
    padding: 16,
  },
  content: {
    flexGrow: 1,
  },
  title: {
    textAlign: 'center',
    alignSelf: 'center',
    marginBottom: 16,
    maxWidth: 330,
  },
  scrollList: {
    flexGrow: 1,
  },
  cardList: {
    gap: 12,
  },
  seeAllButton: {
    marginTop: 24,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
})
